import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useAuth } from '../auth/useAuth'
import { PropertyFormDialog } from './PropertyFormDialog'
import { createProperty, getOrganizationAccess, listProperties, updateProperty } from './property-service'
import type { Property, PropertyFormValues } from './property-types'
import { filterProperties, type PropertyStatusFilter } from './property-filters'
import { getErrorMessage } from '../../lib/error-message'

function formatStatus(status: Property['status']) {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export function PropertiesPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [dialogState, setDialogState] = useState<{ open: boolean; property: Property | null }>({ open: false, property: null })
  const [saveError, setSaveError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<PropertyStatusFilter>('all')
  const [message, setMessage] = useState<string | null>(null)

  const accessQuery = useQuery({ queryKey: ['organization-access', user?.id], queryFn: getOrganizationAccess, enabled: Boolean(user) })
  const propertiesQuery = useQuery({
    queryKey: ['properties', accessQuery.data?.organizationId],
    queryFn: () => listProperties(accessQuery.data!.organizationId),
    enabled: Boolean(accessQuery.data),
  })
  const canEdit = accessQuery.data?.role !== 'viewer'
  const saveMutation = useMutation({
    mutationFn: async (values: PropertyFormValues) => {
      if (!user || !accessQuery.data) throw new Error('Your authenticated organization could not be resolved.')
      return dialogState.property
        ? updateProperty(dialogState.property.id, values)
        : createProperty(accessQuery.data.organizationId, user.id, values)
    },
    onSuccess: async (property) => {
      await queryClient.invalidateQueries({ queryKey: ['properties', accessQuery.data?.organizationId] })
      setDialogState({ open: false, property: null })
      setSaveError(null)
      setMessage(dialogState.property ? `${property.name} was updated.` : `${property.name} was created.`)
    },
    onError: (error) => setSaveError(getErrorMessage(error, 'Unable to save the property.')),
  })

  const openDialog = (property: Property | null) => {
    setSaveError(null)
    setDialogState({ open: true, property })
  }

  const pageError = accessQuery.error ?? propertiesQuery.error
  const filteredProperties = filterProperties(propertiesQuery.data ?? [], search, statusFilter)

  return (
    <section aria-labelledby="properties-title">
      <p className="eyebrow">Portfolio</p>
      <div className="page-heading">
        <div>
          <h1 id="properties-title">Properties</h1>
          <p className="lede">Create and manage secure property workspaces for {accessQuery.data?.organizationName ?? 'your organization'}.</p>
        </div>
        <button type="button" onClick={() => openDialog(null)} disabled={!canEdit || accessQuery.isLoading}>Add property</button>
      </div>
      {message && <div className="toast toast-success" role="status"><span>{message}</span><button type="button" onClick={() => setMessage(null)} aria-label="Dismiss notification">×</button></div>}
      {(accessQuery.isLoading || propertiesQuery.isLoading) && <div className="page-state" role="status">Loading properties…</div>}
      {pageError && <div className="page-state error-state" role="alert"><h2>Properties could not be loaded</h2><p>{pageError.message}</p></div>}
      {!pageError && !propertiesQuery.isLoading && propertiesQuery.data?.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon" aria-hidden="true">⌂</div>
          <h2>No properties yet</h2>
          <p>Create the first property workspace for your organization.</p>
          {canEdit && <button type="button" onClick={() => openDialog(null)}>Add your first property</button>}
        </div>
      )}
      {propertiesQuery.data && propertiesQuery.data.length > 0 && <div className="property-toolbar"><label className="search-field"><span className="sr-only">Search properties</span><input type="search" placeholder="Search name, address or city" value={search} onChange={(event) => setSearch(event.target.value)} /></label><label className="filter-field"><span className="sr-only">Filter by status</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as PropertyStatusFilter)}><option value="all">All statuses</option><option value="draft">Draft</option><option value="active">Active</option><option value="archived">Archived</option></select></label><span className="result-count">{filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'}</span></div>}
      {propertiesQuery.data && propertiesQuery.data.length > 0 && filteredProperties.length === 0 && <div className="page-state"><h2>No matching properties</h2><p>Adjust the search text or status filter.</p></div>}
      {filteredProperties.length > 0 && (
        <div className="property-grid" aria-label="Properties">
          {filteredProperties.map((property) => (
            <article className="property-card" key={property.id}>
              <div className="property-card-heading">
                <span className={`status status-${property.status}`}>{formatStatus(property.status)}</span>
                {canEdit && <button className="text-action" type="button" onClick={() => openDialog(property)}>Edit</button>}
              </div>
              <h2><Link to="/properties/$propertyId" params={{ propertyId: property.id }}>{property.name}</Link></h2>
              <address>{property.address_line_1}<br />{property.city}, {property.region} {property.postal_code}<br />{property.country_code}</address>
              <p className="updated-at">Updated {new Date(property.updated_at).toLocaleDateString()}</p>
              <Link className="card-link" to="/properties/$propertyId" params={{ propertyId: property.id }}>Open workspace →</Link>
            </article>
          ))}
        </div>
      )}
      {dialogState.open && (
        <PropertyFormDialog
          property={dialogState.property}
          isSaving={saveMutation.isPending}
          saveError={saveError}
          onClose={() => setDialogState({ open: false, property: null })}
          onSave={async (values) => { await saveMutation.mutateAsync(values) }}
        />
      )}
    </section>
  )
}
